import api from './api';

/**
 * Customer service for handling all customer-related API calls
 */
const customerService = {
  /**
   * Get a list of customers with optional filtering, sorting, and pagination
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (starts at 1)
   * @param {number} options.limit - Number of items per page
   * @param {string} options.sort - Sort field and direction (e.g., '-createdAt' for descending by createdAt)
   * @param {Object} options.filter - Filter criteria
   * @returns {Promise<Object>} Paginated result with items and metadata
   */
  getCustomers: async (options = {}) => {
    try {
      const { page = 1, limit = 10, sort, filter = {} } = options;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      if (sort) {
        params.append('sort', sort);
      }
      
      // Add any filter criteria
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
      
      const response = await api.get(`/customers?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch customers');
    }
  },
  
  /**
   * Get a customer by ID
   * @param {string} id - The customer ID
   * @returns {Promise<Object>} Customer data
   */
  getCustomerById: async (id) => {
    try {
      const response = await api.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch customer');
    }
  },
  
  /**
   * Create a new customer
   * @param {Object} customerData - The customer data
   * @returns {Promise<Object>} Created customer data
   */
  createCustomer: async (customerData) => {
    try {
      const response = await api.post('/customers', customerData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to create customer');
    }
  },
  
  /**
   * Update an existing customer
   * @param {string} id - The customer ID
   * @param {Object} customerData - The updated customer data
   * @returns {Promise<Object>} Updated customer data
   */
  updateCustomer: async (id, customerData) => {
    try {
      const response = await api.put(`/customers/${id}`, customerData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update customer');
    }
  },
  
  /**
   * Delete a customer
   * @param {string} id - The customer ID
   * @returns {Promise<boolean>} True if deletion was successful
   */
  deleteCustomer: async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete customer');
    }
  },
  
  /**
   * Get customer statistics (for dashboard)
   * @returns {Promise<Object>} Customer statistics
   */
  getCustomerStats: async () => {
    try {
      const response = await api.get('/customers/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch customer statistics');
    }
  },
  
  /**
   * Export customers data (CSV/Excel)
   * @param {Object} options - Export options
   * @param {string} options.format - Export format (csv, excel)
   * @param {Object} options.filter - Filter criteria
   * @returns {Promise<Blob>} Exported file as Blob
   */
  exportCustomers: async (options = { format: 'csv' }) => {
    try {
      const { format = 'csv', filter = {} } = options;
      
      // Build query parameters
      const params = { format };
      
      // Add filter criteria
      if (filter.country) params.country = filter.country;
      if (filter.province) params.province = filter.province;
      if (filter.state) params.state = filter.state;
      if (filter.search) params.search = filter.search;
      
      // Set response type based on format
      const responseType = 'blob';
      const headers = {
        'Accept': format === 'csv' ? 'text/csv' : 'application/json'
      };
      
      const response = await api.get('/customers/export', {
        params,
        responseType,
        headers
      });
      
      return response.data;
    } catch (error) {
      console.error('Export error:', error);
      throw new Error('Failed to export customers');
    }
  }
};

export default customerService;